import { LEARNING_REQUIREMENTS } from "@/lib/types/comms";
import { cn } from "@/lib/utils";
import { useFormContext } from "react-hook-form";
import { FormField, FormItem, FormLabel, FormControl } from "../ui/form";
import { Input } from "../ui/input";
import { RadioGroup, RadioGroupItem } from "../ui/radio-group";
import { CheckCircle } from "@phosphor-icons/react";
import { useTranslation } from "react-i18next";

const CHECKBOX_UNSSELECTED_STYLES =
  "px-3 py-2 font-light bg-white rounded-lg transition-all whitespace-nowrap cursor-pointer border flex flex-row items-center h-full !text-small-content !leading-[normal]";
const CHECKBOX_SELECTED_STYLES =
  "bg-secondary !font-semibold text-secondary-foreground";

function RadioOption({
  name,
  value,
  label,
}: {
  name: string;
  value: string;
  label: string;
}) {
  const form = useFormContext();
  const currentValue = form.watch(name);
  const isSelected = currentValue === value;

  return (
    <FormItem key={value}>
      <FormControl>
        <RadioGroupItem value={value} className="hidden" />
      </FormControl>
      <FormLabel
        className={cn(
          CHECKBOX_UNSSELECTED_STYLES,
          isSelected && CHECKBOX_SELECTED_STYLES
        )}
      >
        <span
          className={cn(
            "inline-flex items-center justify-center mr-2 transition-[width] duration-200 ease-in-out",
            isSelected ? "opacity-100 w-5 h-5" : "opacity-0 w-0 h-0 mr-0"
          )}
        >
          <CheckCircle weight="bold" className="font-bold" />
        </span>
        {label}
      </FormLabel>
    </FormItem>
  );
}

export function OptionGroup({
  name,
  options,
  label,
  otherKey = "OTHER",
  skipKey = "UNSET",
  skipUnset = true,
}: {
  name: string;
  options: Record<string, string>;
  label?: string;
  otherKey?: string;
  skipKey?: string;
  skipUnset?: boolean;
}) {
  const { t } = useTranslation("common");
  const form = useFormContext();
  const currentValue = form.watch(name);
  const validOptions = Object.values(options);
  const isValidOption = validOptions.includes(currentValue);
  const isOther = currentValue === options[otherKey] || !isValidOption;

  return (
    <>
      <FormField
        control={form.control}
        name={name}
        render={({ field }) => (
          <FormItem className="mt-4">
            {label && <FormLabel>{label}</FormLabel>}
            <FormControl className="mt-4">
              <RadioGroup
                onValueChange={field.onChange}
                defaultValue={field.value}
                className="flex flex-row flex-wrap w-full gap-2"
              >
                {Object.values(options).map((option) => {
                  if (option === options[skipKey] && skipUnset) return null;
                  return (
                    <RadioOption
                      key={option}
                      name={name}
                      value={option}
                      label={t(option)}
                    />
                  );
                })}
              </RadioGroup>
            </FormControl>
          </FormItem>
        )}
      />

      {isOther && (
        <FormField
          control={form.control}
          name={name}
          render={({ field }) => (
            <FormItem className="mt-4">
              <FormControl>
                <Input
                  placeholder={t("a11y.other.placeholder")}
                  {...field}
                  value={field.value === options[otherKey] ? "" : field.value}
                />
              </FormControl>
            </FormItem>
          )}
        />
      )}
    </>
  );
}

function A11yOptions({
  requirementRequired = true,
}: {
  requirementRequired?: boolean;
  ageRequired?: boolean;
}) {
  const { t } = useTranslation("common");
  const form = useFormContext();

  return (
    <>
      {form.watch("learning_requirement") !== undefined && (
        <>
          <p className="text-sm text-muted-foreground mt-2">
            {t("a11y.notice")}
          </p>
          <OptionGroup
            name="learning_requirement"
            options={LEARNING_REQUIREMENTS}
            skipUnset={requirementRequired}
          />
        </>
      )}
    </>
  );
}

export default A11yOptions;
